import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, can, getUser } from '../../lib/api';
import { day, dateTime } from '../../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, Modal, Pill, useResource, useToast } from '../../components/ui';
import { plural } from './types';

/**
 * The complaint check. A person says the site tracked more than they allowed;
 * this screen puts the record beside the scan: what they and every browser
 * joined to them had chosen on the day, what the website was setting around
 * that day, and a verdict per category. Red only where it matters.
 */

type Verdict = 'matches' | 'set_despite_rejection' | 'set_before_choice' | 'not_governed';
type Choice = 'allowed' | 'refused' | 'withdrawn' | 'expired' | 'no_choice';

interface Subject { kind: 'person' | 'browser'; hashPrefix: string; identifierType: string | null; via: string | null; linkedAt: string | null }
interface ChoiceRow {
  subject: 'person' | 'browser'; subjectPrefix: string; category: string; purposeId: string; purposeName: string; choice: Choice;
  decidedAt: string; channel: string; noticeVersion: number | null; geoModel: string | null; bannerAction: string | null;
}
interface ScanCookie { name: string; vendor: string; kind: 'cookie' | 'tag'; setBeforeConsent: boolean | null; reported: boolean }
interface ScanView { id: string; mode: 'html' | 'browser'; startedAt: string; distanceDays: number; urls: string[]; pagesFetched: number; byCategory: Array<{ category: string; cookies: ScanCookie[] }> }
interface VerdictRow {
  category: string; purposeName: string | null; governed: boolean; choice: Choice; cookiesSet: number; setBeforeConsent: number;
  evidence: 'browser' | 'html' | 'none'; scanDistanceDays: number | null; verdict: Verdict; basis: string;
}
interface Result {
  on: string;
  identity: { identifierType: string | null; hashed: true; hashPrefix: string; ipStored: false; linkedBrowsers: number };
  subjects: Subject[];
  choices: ChoiceRow[];
  scans: { before: ScanView | null; after: ScanView | null };
  verdicts: VerdictRow[];
  reported: { names: string[]; seen: string[]; notSeen: string[] };
  generatedAt: string;
}
interface Incident { request: { id: string; reference: string; dueBy: string }; attachmentId: string | null; draftReply: string | null }
interface GrievanceRow { id: string; reference: string; kind: string; status: string; principalHash: string; receivedAt: string; details: Record<string, unknown> }

type Who = 'email' | 'phone' | 'customer_id' | 'visitor_id' | 'reference';

const WHO_LABEL: Record<Who, string> = {
  email: 'Email address', phone: 'Mobile number', customer_id: 'Customer id', visitor_id: 'Browser id', reference: 'Reference',
};
const CATEGORY_LABEL: Record<string, string> = {
  essential: 'Strictly necessary', functional: 'Functional', analytics: 'Analytics', marketing: 'Marketing', video: 'Video', unknown: 'Not classified',
};
const CHOICE_LABEL: Record<Choice, string> = { allowed: 'Allowed', refused: 'Refused', withdrawn: 'Withdrawn', expired: 'Expired', no_choice: 'No choice recorded' };
const VERDICT_LABEL: Record<Verdict, string> = {
  matches: 'Matches the choice', set_despite_rejection: 'Set despite rejection', set_before_choice: 'Set before any choice', not_governed: 'Not governed by a choice',
};
const IDENTIFIER_LABEL: Record<string, string> = { email: 'Email address', phone: 'Mobile number', customer_id: 'Customer id', visitor_id: 'Browser' };

const today = () => new Date().toISOString().slice(0, 10);
const isRed = (v: Verdict) => v === 'set_despite_rejection' || v === 'set_before_choice';

export default function Complaint() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const user = getUser();
  const canRaise = can(user, 'requests:handle');
  const canSeeRequests = can(user, 'requests:view');

  const [who, setWho] = useState<Who>((params.get('identifierType') as Who | null) ?? (params.get('reference') ? 'reference' : 'email'));
  const [value, setValue] = useState(params.get('identifier') ?? params.get('reference') ?? '');
  const [on, setOn] = useState(params.get('on') ?? today());
  const [reportedText, setReportedText] = useState(params.get('text') ?? '');
  const [reportedNames, setReportedNames] = useState<string[]>((params.get('cookies') ?? '').split(',').map((s) => s.trim()).filter(Boolean));
  const [fromRequest, setFromRequest] = useState<string | null>(params.get('request'));

  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [incident, setIncident] = useState<(Incident & { mode: 'reply' | 'record' }) | null>(null);

  const grievances = useResource<{ requests: GrievanceRow[] }>(
    () => (canSeeRequests ? api.get('/v1/requests?status=open&kind=grievance') : Promise.resolve({ requests: [] })), [],
  );
  const reported = (grievances.data?.requests ?? []).filter((r) => r.details?.category === 'unwanted_tracking');

  const query = (names: string[]) => {
    const q = new URLSearchParams();
    if (who === 'reference') q.set('reference', value.trim());
    else { q.set('identifier', value.trim()); q.set('identifierType', who); }
    q.set('on', on);
    if (names.length) q.set('cookies', names.join(','));
    return q.toString();
  };

  async function check(names = reportedNames) {
    if (!value.trim() || !on) return;
    setChecking(true);
    setError(null);
    setIncident(null);
    try {
      const r = await api.get<Result>(`/v1/cookies/complaint?${query(names)}`);
      setResult(r);
      const next = new URLSearchParams(params);
      next.set('view', 'complaint');
      if (who === 'reference') { next.set('reference', value.trim()); next.delete('identifier'); next.delete('identifierType'); }
      else { next.set('identifier', value.trim()); next.set('identifierType', who); next.delete('reference'); }
      next.set('on', on);
      if (names.length) next.set('cookies', names.join(',')); else next.delete('cookies');
      setParams(next, { replace: true });
    } catch (err) {
      setResult(null);
      setError((err as Error).message);
    } finally {
      setChecking(false);
    }
  }

  // Opened from a grievance: run at once.
  useEffect(() => {
    if ((params.get('reference') || params.get('identifier')) && params.get('on')) void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openGrievance(g: GrievanceRow) {
    const names = Array.isArray(g.details.cookies) ? (g.details.cookies as string[]) : [];
    setWho('reference');
    setValue(g.principalHash.slice(0, 12));
    setOn(g.receivedAt.slice(0, 10));
    setReportedText(String(g.details.description ?? ''));
    setReportedNames(names);
    setFromRequest(g.reference);
    setResult(null);
    setTimeout(() => void checkWith('reference', g.principalHash.slice(0, 12), g.receivedAt.slice(0, 10), names), 0);
  }

  async function checkWith(w: Who, v: string, d: string, names: string[]) {
    setChecking(true);
    setError(null);
    setIncident(null);
    try {
      const q = new URLSearchParams();
      if (w === 'reference') q.set('reference', v); else { q.set('identifier', v); q.set('identifierType', w); }
      q.set('on', d);
      if (names.length) q.set('cookies', names.join(','));
      setResult(await api.get<Result>(`/v1/cookies/complaint?${q.toString()}`));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setChecking(false);
    }
  }

  // Cookie names the person wrote about, found in the description text.
  const highlighted = useMemo(() => {
    const set = new Set(reportedNames.map((n) => n.toLowerCase()));
    if (result && reportedText) {
      const text = reportedText.toLowerCase();
      for (const s of [result.scans.before, result.scans.after]) {
        for (const g of s?.byCategory ?? []) for (const c of g.cookies) if (c.name.length >= 3 && text.includes(c.name.toLowerCase())) set.add(c.name.toLowerCase());
      }
    }
    return set;
  }, [result, reportedText, reportedNames]);

  async function raise(mode: 'reply' | 'record') {
    if (!result) return;
    setBusy(mode);
    try {
      const body: Record<string, unknown> = {
        on: result.on, cookies: reportedNames, attachEvidence: true, draftReply: mode === 'reply',
        ...(fromRequest ? { note: `Reported by the person as ${fromRequest}.` } : {}),
      };
      if (who === 'reference') body.reference = value.trim(); else { body.identifier = value.trim(); body.identifierType = who; }
      const out = await api.post<Incident>('/v1/cookies/complaint/incident', body);
      setIncident({ ...out, mode });
      toast(`Grievance ${out.request.reference} opened with the evidence sheet attached.`, 'ok');
      grievances.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  async function exportSheet() {
    setBusy('export');
    try {
      await api.download(`/v1/cookies/complaint.pdf?${query(reportedNames)}`, `cookie-complaint-check-${on}.pdf`);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  const red = result?.verdicts.filter((v) => isRed(v.verdict)) ?? [];
  const firstRed = red[0]?.category;

  return (
    <>
      <Card title="Check a complaint">
        <form className="ck-complaint-form" onSubmit={(e) => { e.preventDefault(); void check(); }}>
          <Field label="Identify the person by">
            <select value={who} onChange={(e) => setWho(e.target.value as Who)} aria-label="Identify the person by">
              {(Object.keys(WHO_LABEL) as Who[]).map((k) => <option key={k} value={k}>{WHO_LABEL[k]}</option>)}
            </select>
          </Field>
          <Field label={WHO_LABEL[who]} help={who === 'reference' ? 'The first characters of the reference shown in the preference centre.' : 'Hashed on the server; the value is not stored by this check.'}>
            <input
              type={who === 'email' ? 'email' : 'text'} value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={who === 'email' ? 'name@example.com' : who === 'phone' ? '98765 43210' : who === 'reference' ? '12 characters' : ''}
              autoComplete="off"
            />
          </Field>
          <Field label="Date of the complaint" help="Choices as they stood at the end of this day, UTC.">
            <input type="date" value={on} max={today()} onChange={(e) => setOn(e.target.value)} />
          </Field>
          <button className="primary" type="submit" disabled={checking || !value.trim() || !on}>{checking ? 'Checking' : 'Check'}</button>
        </form>
        <ErrorNote error={error} />
        {(reportedText || reportedNames.length > 0) && (
          <p className="ck-facts">
            {fromRequest && <span>From grievance {fromRequest}</span>}
            {reportedNames.length > 0 && <span>Cookies reported: {reportedNames.join(', ')}</span>}
            {reportedText && <span>Report: {reportedText.length > 160 ? `${reportedText.slice(0, 160)}...` : reportedText}</span>}
            <button type="button" className="ck-link" onClick={() => { setReportedText(''); setReportedNames([]); setFromRequest(null); }}>Clear</button>
          </p>
        )}
      </Card>

      {!result && canSeeRequests && (
        <Card title="Reported from the preference centre">
          {grievances.loading && !grievances.data && <Loading what="Loading reports" />}
          {grievances.data && reported.length === 0 && <Empty>No open report of unwanted tracking.</Empty>}
          {reported.length > 0 && (
            <div className="table-scroll">
              <table className="data ck-table ck-reported-list">
                <thead><tr><th>Reference</th><th>Received</th><th>Person</th><th>Report</th><th /></tr></thead>
                <tbody>
                  {reported.map((g) => (
                    <tr key={g.id}>
                      <td><strong>{g.reference}</strong></td>
                      <td className="small">{day(g.receivedAt)}</td>
                      <td className="small"><span className="mono">{g.principalHash.slice(0, 12)}</span> hashed</td>
                      <td className="small">{String(g.details.description ?? '')}</td>
                      <td><button className="small" onClick={() => openGrievance(g)}>Check</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {result && (
        <>
          <dl className="ck-identity">
            <div><dt>Person</dt><dd>{IDENTIFIER_LABEL[result.identity.identifierType ?? ''] ?? 'Identifier'}, hashed <span className="mono">{result.identity.hashPrefix}</span></dd></div>
            <div><dt>Browsers linked</dt><dd>{result.identity.linkedBrowsers}</dd></div>
            <div><dt>IP address</dt><dd>Not stored</dd></div>
            <div><dt>Date checked</dt><dd>{day(result.on)} (UTC)</dd></div>
            <div><dt>Scans used</dt><dd>{scanWord(result.scans.before, 'before')}, {scanWord(result.scans.after, 'after')}</dd></div>
            {result.reported.names.length > 0 && (
              <div><dt>Cookies reported</dt><dd>{result.reported.seen.length} of {result.reported.names.length} seen in the scans{result.reported.notSeen.length ? `; not seen: ${result.reported.notSeen.join(', ')}` : ''}</dd></div>
            )}
          </dl>

          <div className="ck-actions">
            {canRaise && <button className="primary" onClick={() => raise('reply')} disabled={busy !== null}>{busy === 'reply' ? 'Opening' : 'Reply to the person'}</button>}
            {canRaise && <button onClick={() => raise('record')} disabled={busy !== null}>{busy === 'record' ? 'Opening' : 'Open grievance record'}</button>}
            <button type="button" onClick={() => navigate(`/cookies?view=banner${firstRed ? `&category=${firstRed}` : ''}`)}>Fix the tag</button>
            <button onClick={exportSheet} disabled={busy !== null}>{busy === 'export' ? 'Preparing' : 'Export'}</button>
          </div>

          <Card title="Verdict by category">
            <div className="table-scroll">
              <table className="data ck-table ck-wrap">
                <thead>
                  <tr><th>Category</th><th>Choice on the day</th><th className="num">Cookies set</th><th>Scan</th><th>Verdict</th><th>Basis</th></tr>
                </thead>
                <tbody>
                  {result.verdicts.map((v) => (
                    <tr key={v.category}>
                      <td>
                        {CATEGORY_LABEL[v.category] ?? v.category}
                        <div className="small muted">{v.purposeName ?? 'No purpose mapped'}</div>
                      </td>
                      <td className="small">{v.governed ? CHOICE_LABEL[v.choice] : <span className="muted">Not asked</span>}</td>
                      <td className="num">
                        {v.cookiesSet}
                        <div className="small muted">{v.evidence === 'browser' ? `${v.setBeforeConsent} before consent` : v.evidence === 'html' && v.cookiesSet > 0 ? 'before consent: cannot tell' : ''}</div>
                      </td>
                      <td className="small">{v.scanDistanceDays === null ? <span className="muted">No scan</span> : `${plural(v.scanDistanceDays, 'day')} away`}</td>
                      <td>
                        {isRed(v.verdict) ? <Pill tone="bad">{VERDICT_LABEL[v.verdict]}</Pill>
                          : v.verdict === 'not_governed' ? <span className="ck-verdict-muted">{VERDICT_LABEL[v.verdict]}</span>
                            : v.evidence === 'html' && v.cookiesSet > 0 && v.choice !== 'allowed' ? <span className="ck-verdict-muted">Not checked by a browser crawl</span>
                              : <span className="ck-verdict-plain">{VERDICT_LABEL[v.verdict]}</span>}
                      </td>
                      <td className="ck-verdict-basis">{v.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid cols-2 ck-grid" style={{ marginTop: 14 }}>
            <Card title="Choices on record">
              {result.choices.length === 0 ? <Empty>No cookie choice recorded on or before this date.</Empty> : (
                <div className="table-scroll">
                  <table className="data ck-table ck-wrap">
                    <thead><tr><th>Who</th><th>Category</th><th>Choice</th><th>Decided</th><th>Notice</th></tr></thead>
                    <tbody>
                      {result.choices.map((c, i) => (
                        <tr key={`${c.subjectPrefix}-${c.purposeId ?? i}`}>
                          <td className="small">{c.subject === 'person' ? 'Person' : <>Browser <span className="mono">{c.subjectPrefix}</span></>}</td>
                          <td>{CATEGORY_LABEL[c.category] ?? c.category}</td>
                          <td>
                            {CHOICE_LABEL[c.choice]}
                            <div className="small muted">{[c.channel === 'cookie' ? 'banner' : c.channel, c.bannerAction, c.geoModel].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td className="small ck-nowrap">{dateTime(c.decidedAt)}</td>
                          <td className="small">{c.noticeVersion ? `v${c.noticeVersion}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card title="Browsers on this record">
              <div className="table-scroll">
                <table className="data ck-table ck-wrap">
                  <thead><tr><th>Who</th><th>Reference</th><th>Joined</th></tr></thead>
                  <tbody>
                    {result.subjects.map((s) => (
                      <tr key={`${s.kind}-${s.hashPrefix}`}>
                        <td>{s.kind === 'person' ? (IDENTIFIER_LABEL[s.identifierType ?? ''] ?? 'Person') : 'Browser'}</td>
                        <td><span className="mono">{s.hashPrefix}</span> <span className="muted">hashed</span></td>
                        <td className="small">{s.linkedAt ? `${day(s.linkedAt)}${s.via ? ` by ${s.via.replace(/_/g, ' ')}` : ''}` : <span className="muted">Starting point</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ck-legend"><span>A browser is one wk_visitor cookie, hashed. No IP address or device identifier is read.</span></p>
            </Card>
          </div>

          <div className="grid cols-2 ck-grid">
            <ScanCard title="Scan nearest before" scan={result.scans.before} highlighted={highlighted} />
            <ScanCard title="Scan nearest after" scan={result.scans.after} highlighted={highlighted} />
          </div>
          <p className="ck-legend">
            <span><span className="ck-cookie before">name</span> set before any choice in a browser crawl</span>
            <span><span className="ck-cookie reported">name</span> reported by the person</span>
            <span>A page HTML scan lists what the markup carries and cannot tell whether a tag waited for the choice.</span>
          </p>
        </>
      )}

      {incident && (
        <Modal
          wide={incident.mode === 'reply'}
          title={incident.mode === 'reply' ? `Reply to the person · ${incident.request.reference}` : `Grievance ${incident.request.reference} opened`}
          onClose={() => setIncident(null)}
          footer={
            <>
              {incident.draftReply && (
                <button onClick={() => { void navigator.clipboard?.writeText(incident.draftReply ?? ''); toast('Draft copied.', 'ok'); }}>Copy draft</button>
              )}
              <button type="button" onClick={() => navigate('/requests')}>Open rights requests</button>
              <button className="primary" onClick={() => setIncident(null)}>Done</button>
            </>
          }
        >
          <dl className="kv">
            <dt>Reference</dt><dd>{incident.request.reference}</dd>
            <dt>Due by</dt><dd>{day(incident.request.dueBy)}</dd>
            <dt>Evidence</dt><dd>{incident.attachmentId ? 'Complaint check sheet attached to the grievance' : 'No sheet attached'}</dd>
          </dl>
          {incident.draftReply && (
            <Field label="Draft reply" help="Plain text for the grievance officer to send. The reference goes in the subject line.">
              <textarea className="ck-draft" readOnly value={incident.draftReply} />
            </Field>
          )}
        </Modal>
      )}
    </>
  );
}

function scanWord(s: ScanView | null, side: 'before' | 'after'): string {
  if (!s) return `none ${side}`;
  return `${day(s.startedAt)} (${plural(s.distanceDays, 'day')} ${side})`;
}

function ScanCard({ title, scan, highlighted }: { title: string; scan: ScanView | null; highlighted: Set<string> }) {
  if (!scan) return <Card title={title}><Empty>No completed scan on that side of the date.</Empty></Card>;
  const total = scan.byCategory.reduce((n, g) => n + g.cookies.length, 0);
  const before = scan.byCategory.reduce((n, g) => n + g.cookies.filter((c) => c.setBeforeConsent).length, 0);
  return (
    <Card title={title}>
      <div className="ck-scan-head">
        <span>{dateTime(scan.startedAt)}</span>
        <span>{scan.mode === 'browser' ? 'Browser crawl' : 'Page HTML'}</span>
        <span>{plural(scan.distanceDays, 'day')} from the date</span>
        <span>{plural(scan.pagesFetched, 'page')}</span>
        <span>{plural(total, 'cookie')}{scan.mode === 'browser' ? `, ${before} before consent` : ''}</span>
      </div>
      {scan.byCategory.length === 0 ? <Empty>No cookie or tag seen.</Empty> : scan.byCategory.map((g) => (
        <div className="ck-scan-cat" key={g.category}>
          <div className="ck-scan-cat-name">{CATEGORY_LABEL[g.category] ?? g.category}</div>
          <div className="ck-scan-cells">
            {g.cookies.map((c) => (
              <span
                key={`${c.kind}-${c.name}`}
                className={`ck-cookie${c.setBeforeConsent ? ' before' : ''}${c.reported || highlighted.has(c.name.toLowerCase()) ? ' reported' : ''}`}
                title={c.kind === 'tag' ? 'Tag found in the page; its cookies were not seen' : c.setBeforeConsent === null ? 'A page HTML scan cannot tell whether this waited for the choice' : c.setBeforeConsent ? 'Set before any choice' : 'Set only after consent'}
              >
                {c.name}
                {c.vendor && c.vendor !== c.name && <span className="ck-cookie-vendor">{c.vendor}</span>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}
